import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as actions from '~/store/actions';
import * as NuacomComponent from '~/components';
import { _, ToastMessage } from '~/dependencies';
import { useNavigation, useConversation } from '~/hooks';
import { ChannelTypeEnum } from '~/enums/conversations';
import {
  ContactOrGroup,
  Contact,
  ContactGroup,
  SmsTemplate,
  SmsSender,
} from '~/types/accountInfo';
import { log } from '~/helpers';
import {
  isContact,
  isContactGroup,
} from '../components/RecipientsSelector/utils';
import RecipientsSelector from '../components/RecipientsSelector';
import SenderSelector from '../components/SenderSelector';
import MessageTextInput from '../components/MessageTextInput';
import ConversationWarnings from '../components/ConversationWarnings';
import {
  Container,
  Right,
  Block,
  MessageHeader,
  MessageContent,
} from './styles';

/**
 * The maximum number of recipients that can be selected.
 */
const MAX_PARTICIPANTS = 10;

export default function New(props: any) {
  // HOOK'S
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const {
    loading,
    conversationId,
    submitted,
    setSubmitted,
    errorMessage,
    ...conversation
  } = useConversation();
  const location = useLocation() as ReturnType<typeof useLocation> & {
    state: {
      id?: number;
      name?: string | null;
      mobile: string;
    };
  };

  // STATE'S
  const [channelType] = useState<ChannelTypeEnum>(1);
  const [recipientsList, setRecipientsList] = useState([]);
  const [invalidRecipientsList, setInvalidRecipientsList] = useState([]);
  const [template, setTemplate] = useState<SmsTemplate | null>(null);
  const [sender, setSender] = useState<SmsSender | null>(null);

  const hasInvalidRecipients = invalidRecipientsList.length > 0;
  const hasNotSenderOrTemplateSelected = sender === null && template === null;

  // If list is empty or has invalid recipients, disable the send message button
  const isSendMessageBtnDisabled =
    _.isEmpty(recipientsList) ||
    hasInvalidRecipients ||
    hasNotSenderOrTemplateSelected;

  // CONST'S
  const initialRecipientsList = location.state?.mobile
    ? [
        {
          id: location.state.id ?? 0, // Ensure id is a number, fallback to 0 if undefined
          name: location.state.name ?? '', // Ensure name is always a string
          mobile: location.state.mobile,
        },
      ]
    : [];

  // EFFECT'S
  useEffect(() => {
    dispatch(actions.setHiddenBottomNav(true));

    return () => {
      dispatch(actions.setHiddenBottomNav(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!loading && submitted) {
      if (errorMessage) {
        ToastMessage('error', errorMessage);
        setSubmitted(false);
      } else if (conversationId) {
        navigate.chatView({ id: conversationId });
      } else if (recipientsList.length > 1) {
        // If multiple recipients, show success message and navigate back to conversations list
        ToastMessage('success', 'Messages sent successfully');
        setTimeout(() => navigate.conversations(), 300);
      } else {
        navigate.conversations();
      }
    }
  }, [
    loading,
    submitted,
    setSubmitted,
    conversationId,
    errorMessage,
    channelType,
    recipientsList,
    sender,
    navigate,
  ]);

  const handleSendMessage = async (message: string) => {
    try {
      // The list of phone number to receive the message e.g, "+3530000000,+35311111111"
      const contacts: string = recipientsList
        .filter((participant: ContactOrGroup) => isContact(participant))
        // @ts-ignore
        .map(({ mobile }: Contact) => mobile)
        .join(',');

      // The list of group IDs to receive the message e.g, "1,2,3"
      const groups: string = recipientsList
        .filter((participant: ContactOrGroup) => isContactGroup(participant))
        // @ts-ignore
        .map(({ groupId }: ContactGroup) => groupId)
        .join(',');

      const templateId = template?.id;
      const senderName = sender?.name;
      const senderId = templateId ? undefined : senderName;

      await conversation.create({
        channelType,
        contacts,
        groups,
        senderId,
        senderName,
        templateId,
        message,
      });
    } catch (err) {
      log.error('Failed to create conversation. Reason: ', err);
    }
  };

  /**
   * When a template is appended to the message input, set its senders as default selected senders
   */
  const handleOnTemplateAppend = (smsTemplate: SmsTemplate) => {
    setTemplate(smsTemplate);
    if (smsTemplate.usage === '10dlc') {
      setSender(null);
    }
  };

  const componentInfoLogger = () => {
    log.debug('New Conversations Info: ', {
      channelType,
      recipientsList,
      invalidRecipientsList,
      template,
      sender,
      loading,
      conversationId,
      submitted,
      errorMessage,
    });
  };

  return (
    <Container onDoubleClick={componentInfoLogger}>
      <NuacomComponent.Header {...props} center>
        <Right
          data-test-id="cancel-new-conversation"
          onClick={() => navigate.conversations()}>
          {t('screens.conversation.cancel')}
        </Right>
      </NuacomComponent.Header>

      <Block className="parent-scrollable-container">
        <MessageHeader>
          <RecipientsSelector
            sender={sender}
            template={template}
            initialValues={initialRecipientsList}
            onChange={setRecipientsList}
            onValidChange={setInvalidRecipientsList}
            maxRecipients={MAX_PARTICIPANTS}
          />
          <SenderSelector
            defaultSenders={template?.senderNames || []}
            onChange={value => setSender(value)}
          />
        </MessageHeader>
        <ConversationWarnings
          invalidRecipients={invalidRecipientsList}
          recipients={recipientsList}
          template={template}
          maxRecipients={MAX_PARTICIPANTS}
        />
        <MessageContent>
          <MessageTextInput
            channelType={channelType}
            onSend={handleSendMessage}
            onTemplateAppend={handleOnTemplateAppend}
            isLoading={loading}
            disabled={isSendMessageBtnDisabled}
          />
        </MessageContent>
      </Block>
    </Container>
  );
}
